import type {
  ApiBlockDataRequest,
  FilterRequestApiDto,
  FilterResponseApiDto,
  ApiTableBlockResponse,
  ApiChartBlockResponse,
  ApiSummaryCardBlockResponse,
} from '@reports/infrastructure/api/types';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import type {
  ReportFilterConfig,
  ReportFilterOptions,
  TableBlockData,
  ChartBlockData,
  SummaryCardBlockData,
} from '@reports/domain/value-objects';
import {
  createReportFilterOptions,
  getDateFormatted,
  createTableBlockData,
  createChartBlockData,
  createSummaryCardBlockData,
} from '@reports/domain/value-objects';
import type { ReportApiMapperType } from './types';

export const createReportApiMapper = (): ReportApiMapperType => {
  /**
   * Maps ReportFilterConfig array to API request format
   *
   * Extracts filter keys from configurations for API request
   *
   * @param filterConfigs - Array of filter configurations from report definition
   * @returns API request with filter keys
   */
  const mapFilterConfigToRequestApiDto = (filterConfigs: ReadonlyArray<ReportFilterConfig>): FilterRequestApiDto => {
    const filters = filterConfigs.map((config) => config.key);
    return { filters };
  };

  /**
   * Maps API response to ReportFilterOptions array using Value Object factory
   *
   * Validates and creates immutable Value Objects from API response
   *
   * @param response - API response with filter options
   * @returns Array of validated ReportFilterOptions Value Objects
   */
  const mapFilterResponseApiDtoToFilterOptions = (
    response: FilterResponseApiDto,
  ): ReadonlyArray<ReportFilterOptions> => {
    return response.data.map((filter) =>
      createReportFilterOptions({
        type: filter.type,
        optionValues: [...filter.optionValues],
      }),
    );
  };

  /**
   * Maps BlockDataRequestDto to API request format
   * Converts Date objects to string format (YYYY-MM-DD) for API communication
   * Passes through pagination parameters if present
   */
  const mapBlockDataRequestDtoToApiRequest = (request: BlockDataRequestDto): ApiBlockDataRequest => {
    return {
      startDate: getDateFormatted(request.startDate),
      endDate: getDateFormatted(request.endDate),
      startHour: request.startHour,
      endHour: request.endHour,
      filters: request.filters,
      page: request.page,
      pageSize: request.pageSize,
    };
  };

  const mapApiTableBlockResponseToTableBlockData = (response: ApiTableBlockResponse): TableBlockData => {
    const data = response.data;
    const pagination = response?.pagination;

    const tableBlockData = createTableBlockData({
      columns: data.columns.map((column) => ({ key: column.key, value: column.displayName })),
      rows: data.rows.map((row) => row.map((cell) => cell)),
      pagination: { next: pagination?.next, prev: pagination?.prev },
    });

    return tableBlockData;
  };

  const mapApiChartBlockResponseToChartBlockData = (response: ApiChartBlockResponse): ChartBlockData => {
    const data = response.data;

    const chartBlockData = createChartBlockData({
      key: data.key,
      series: data.series.map((series) => series),
      values: data.values.map((value) => ({ x: value.x, y: value.y.map((y) => y) })),
    });

    return chartBlockData;
  };

  const mapApiSummaryCardBlockResponseToSummaryCardBlockData = (
    response: ApiSummaryCardBlockResponse,
  ): SummaryCardBlockData => {
    const data = response.data;

    const summaryCardBlockData = createSummaryCardBlockData({
      value: data.value,
      secondaryValue: data.secondaryValue ? data.secondaryValue : undefined,
    });

    return summaryCardBlockData;
  };

  return {
    mapFilterConfigToRequestApiDto,
    mapFilterResponseApiDtoToFilterOptions,
    mapBlockDataRequestDtoToApiRequest,
    mapApiTableBlockResponseToTableBlockData,
    mapApiChartBlockResponseToChartBlockData,
    mapApiSummaryCardBlockResponseToSummaryCardBlockData,
  };
};
